import { useContext, useEffect, useState } from 'react'

import {
  Box,
  List,
  ListItemButton,
  Typography,
  Divider,
  GlobalStyles,
} from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { schema } from '@pkg/zero'
import { Zero, type Schema } from '@rocicorp/zero'
import { ZeroProvider } from '@rocicorp/zero/react'
import {
  IconArrowLeft,
  IconBell,
  IconCode,
  IconCreditCard,
  IconDatabase,
  IconTriangle,
  IconUsers,
  IconWorldWww,
} from '@tabler/icons-react'
import { Toaster } from 'react-hot-toast'

import { routes, Link } from '@redwoodjs/router'
import { useLocation, useParams } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { AvatarCacheProvider } from 'src/components/AvatarCacheProvider/AvatarCacheProvider'
import { ObjectCacheProvider } from 'src/components/ObjectCacheProvider/ObjectCacheProvider'
import ObjectsProvider from 'src/components/Objects/ObjectsProvider'
import { canManageAnyObjectType } from 'src/components/Properties/access'
import Sidebar from 'src/components/Sidebar/Sidebar'
import UserErrorRemediation from 'src/components/UserErrorRemediation/UserErrorRemediation'
import { NavigationProvider } from 'src/contexts/NavigationContext'
import WorkspaceDropdown from 'src/layouts/ModernLayout/WorkspaceDropdown/WorkspaceDropdown'
import { DayContext, DayProvider } from 'src/lib/dayContext'
import { ungatedForAssistant } from 'src/lib/gates'

type WorkspaceSettingsLayoutProps = {
  children?: React.ReactNode
}

const navItemSx = {
  height: '40px',
  borderRadius: '4px',
  my: 0.5,
  px: 2,
  '&:hover': {
    backgroundColor: (theme) => theme.palette.secondary.light,
  },
  '& .MuiTypography-root': {
    fontSize: '14px',
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
  },
}

const navItemActiveSx = {
  ...navItemSx,
  backgroundColor: (theme) => theme.palette.secondary.light,
  '& .MuiTypography-root': {
    fontWeight: 600,
  },
}

const globalStyles = {
  body: {
    overflow: 'hidden',
    overscrollBehaviorX: 'none',
    input: {
      backgroundColor: 'transparent',
    },
  },
}

const createZeroInstance = (
  userId: string | undefined,
  getToken: () => Promise<string | null>
) =>
  new Zero({
    userID: userId ?? 'anonymous',
    auth: userId ? () => getToken() : undefined,
    server:
      process.env.NODE_ENV === 'production'
        ? 'https://sync.prod.day.ai'
        : 'https://sync.dev.day.ai',
    schema,
    kvStore: 'idb',
    logLevel: 'debug',
    hiddenTabDisconnectDelay: 60_000,
  })

const WorkspaceSettingsLayoutInner = ({
  children,
}: WorkspaceSettingsLayoutProps) => {
  const { currentUser } = useAuth()
  const location = useLocation()
  const params = useParams()
  const workspaceId = params.id
  const { workspaces, sidebarObject, setSidebarObject } = useContext(DayContext)

  const workspace = workspaces?.find((w) => w.id === workspaceId)

  if (!workspace) return null

  const members = workspace?.members
  const roles = workspace?.roles

  if (!members || !roles) return null

  const currentUserRoleId = members.find(
    (member) => member.id === currentUser.id
  )?.roleId
  const currentUserRole = roles.find((role) => role.id === currentUserRoleId)

  const canManageBilling =
    currentUserRole?.permissions.some(
      ({ type, permission }) => type === 'BILLING' && permission === 'manage'
    ) && ungatedForAssistant(currentUser)

  const canManageCustomData = canManageAnyObjectType(workspace)

  const containerSx = {
    color: (theme) => theme.palette.text.primary,
    background: (theme) => theme.palette.bottom.main,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    overflow: 'hidden',
    '& .sidebar': {
      color: (theme) => theme.palette.text.primary,
      background: (theme) => theme.palette.bottom.main,
      width: '240px',
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      borderRight: (theme) => `1px solid ${theme.palette.divider}`,
    },
    '& .main-content': {
      width: '100%',
      height: '100vh',
      pl: '240px',
      pr: 0,
      py: 0,
      m: 0,
      pt: 0,
      overflow: 'hidden',
      zIndex: 2,
      background: (theme) => theme.palette.background.paper,
    },
  }

  const getActiveTab = () => {
    // Get the tab parameter from the URL params
    const tab = params.tab

    // Check if it's a valid tab
    const validTabs = [
      'members',
      'billing',
      'domains',
      'integrations',
      'notifications',
      'customData',
      'danger',
    ]
    if (tab && validTabs.includes(tab)) {
      return tab
    }

    // Fallback to checking the pathname
    if (location.pathname.includes('/members')) return 'members'
    if (location.pathname.includes('/billing')) return 'billing'
    if (location.pathname.includes('/domains')) return 'domains'
    if (location.pathname.includes('/integrations')) return 'integrations'
    if (location.pathname.includes('/notifications')) return 'notifications'
    if (location.pathname.includes('/customData')) return 'customData'
    if (location.pathname.includes('/danger')) return 'danger'

    return 'members'
  }

  const activeTab = getActiveTab()

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      <Toaster />
      <CssBaseline />

      <Box sx={containerSx}>
        <Box
          className="sidebar"
          component="div"
        >
          <WorkspaceDropdown forceExpanded={true} />
          <Divider sx={{ mx: 2, mb: 2 }} />

          <List sx={{ p: 2, pt: 1 }}>
            <ListItemButton
              component={Link}
              to={routes.workspaceSettings({ id: workspaceId, tab: 'members' })}
              sx={activeTab === 'members' ? navItemActiveSx : navItemSx}
            >
              <IconUsers
                size={20}
                style={{ marginRight: 12 }}
              />
              <Typography>Members</Typography>
            </ListItemButton>

            {canManageBilling && (
              <ListItemButton
                component={Link}
                to={routes.workspaceSettings({
                  id: workspaceId,
                  tab: 'billing',
                })}
                sx={activeTab === 'billing' ? navItemActiveSx : navItemSx}
              >
                <IconCreditCard
                  size={20}
                  style={{ marginRight: 12 }}
                />
                <Typography>Billing</Typography>
              </ListItemButton>
            )}

            <ListItemButton
              component={Link}
              to={routes.workspaceSettings({ id: workspaceId, tab: 'domains' })}
              sx={activeTab === 'domains' ? navItemActiveSx : navItemSx}
            >
              <IconWorldWww
                size={20}
                style={{ marginRight: 12 }}
              />
              <Typography>Domains</Typography>
            </ListItemButton>

            <ListItemButton
              component={Link}
              to={routes.workspaceSettings({
                id: workspaceId,
                tab: 'integrations',
              })}
              sx={activeTab === 'integrations' ? navItemActiveSx : navItemSx}
            >
              <IconCode
                size={20}
                style={{ marginRight: 12 }}
              />
              <Typography>Integrations</Typography>
            </ListItemButton>

            <ListItemButton
              component={Link}
              to={routes.workspaceSettings({
                id: workspaceId,
                tab: 'notifications',
              })}
              sx={activeTab === 'notifications' ? navItemActiveSx : navItemSx}
            >
              <IconBell
                size={20}
                style={{ marginRight: 12 }}
              />
              <Typography>Notifications</Typography>
            </ListItemButton>

            {canManageCustomData && (
              <ListItemButton
                component={Link}
                to={routes.workspaceSettings({
                  id: workspaceId,
                  tab: 'customData',
                })}
                sx={activeTab === 'customData' ? navItemActiveSx : navItemSx}
              >
                <IconDatabase
                  size={20}
                  style={{ marginRight: 12 }}
                />
                <Typography>Schema</Typography>
              </ListItemButton>
            )}

            <ListItemButton
              component={Link}
              to={routes.workspaceSettings({ id: workspaceId, tab: 'danger' })}
              sx={activeTab === 'danger' ? navItemActiveSx : navItemSx}
            >
              <IconTriangle
                size={20}
                style={{ marginRight: 12 }}
              />
              <Typography>Danger</Typography>
            </ListItemButton>
          </List>

          <Box sx={{ p: 2 }}>
            <ListItemButton
              component={Link}
              to={routes.home()}
              sx={{
                height: '40px',
                borderRadius: '4px',
                px: 2,
                mb: 2,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.secondary.light,
                },
              }}
            >
              <IconArrowLeft
                size={20}
                style={{ marginRight: 8 }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                Back to app
              </Typography>
            </ListItemButton>
          </Box>
        </Box>

        <Box className="main-content">
          <UserErrorRemediation />
          {children}
        </Box>
      </Box>

      <Sidebar
        crmObject={sidebarObject}
        setCrmObject={setSidebarObject}
        onClose={() => {}}
        workspaceId={workspace.id}
      />
    </>
  )
}

const WorkspaceSettingsLayoutWrapper = ({ children, z }) => {
  const { selectedWorkspace } = useContext(DayContext)

  return (
    <ZeroProvider zero={z}>
      <ObjectsProvider workspaceId={selectedWorkspace}>
        <WorkspaceSettingsLayoutInner>{children}</WorkspaceSettingsLayoutInner>
      </ObjectsProvider>
    </ZeroProvider>
  )
}

const WorkspaceSettingsLayout = ({ children }) => {
  const { currentUser, getToken } = useAuth()
  const [z, setZero] = useState<Zero<Schema>>(() =>
    currentUser?.id ? createZeroInstance(currentUser.id, getToken) : null
  )

  useEffect(() => {
    if (!currentUser?.id) {
      return
    }

    if ((currentUser?.id ?? null) !== (z?.userID ?? null)) {
      setZero(createZeroInstance(currentUser?.id, getToken))
    }

    return () => {
      z?.close()
      setZero(null)
    }
  }, [currentUser?.id])

  return (
    currentUser && (
      <DayProvider>
        <NavigationProvider>
          <AvatarCacheProvider>
            <ObjectCacheProvider>
              <WorkspaceSettingsLayoutWrapper z={z}>
                {children}
              </WorkspaceSettingsLayoutWrapper>
            </ObjectCacheProvider>
          </AvatarCacheProvider>
        </NavigationProvider>
      </DayProvider>
    )
  )
}

export default WorkspaceSettingsLayout
